<template>
  <div class="seller-products">
    <v-container fluid class="pa-3 pa-md-6">
      <div class="hero">
        <div class="hero-content">
          <div class="hero-badge">Marketplace</div>
          <h1 class="hero-title">My Products</h1>
          <p class="hero-subtitle">
            Keep listings fresh, track stock, and grow your sales.
          </p>
          <v-btn color="white" class="primary--text" rounded elevation="2"
            :to="{ name: 'business_seller.add_product' }">
            <v-icon left small>mdi-plus</v-icon>
            Add a Product
          </v-btn>
        </div>
        <div class="hero-glow"></div>
      </div>

      <div class="table-section">
        <v-card class="table-card">
          <v-card-title class="table-title">
            <div>
              <div class="title-text">Product List</div>
              <div class="subtitle-text">({{ products.length }}) items in your catalog</div>
            </div>
            <v-spacer />
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search products"
              single-line
              hide-details
              dense
              class="search-field"
            />
          </v-card-title>
          <v-alert v-if="productsError" type="error" dense class="mx-4 mt-2 mb-0">
            {{ productsError }}
          </v-alert>

          <v-progress-linear v-if="loading" indeterminate color="primary" height="3" />
          <div class="table-wrapper">
            <table class="seller-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price (XAF)</th>
                  <th>Wholesale (XAF)</th>
                  <th>Stock</th>
                  <th>Sold</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody v-if="filteredProducts.length">
                <tr v-for="item in filteredProducts" :key="item.id">
                  <td>
                    <v-img
                      :src="item.get_image"
                      max-width="52px"
                      max-height="52px"
                      contain
                      class="thumb"
                    />
                  </td>
                  <td>
                    <router-link
                      :to="'/market_place/items/' + item.id"
                      class="font-weight-semibold text--primary"
                    >
                      {{ item.name }}
                    </router-link>
                  </td>
                  <td>
                    <v-chip x-small>{{ item.category || '-' }}</v-chip>
                  </td>
                  <td>{{ item.price }}</td>
                  <td>{{ item.wholesale_price }}</td>
                  <td>
                    <span :class="item.quantity === 0 ? 'error--text font-weight-bold' : ''">
                      {{ item.quantity }}
                    </span>
                  </td>
                  <td>{{ item.no_bought }}</td>
                  <td>{{ item.get_date_str }}</td>
                  <td>
                    <div class="table-actions">
                      <v-btn x-small text color="primary" :to="'/market_place/items/' + item.id">
                        Preview
                      </v-btn>
                      <v-btn x-small text color="grey darken-2"
                        :to="{ name: 'business_seller.add_product', query: { edit: item.id } }">
                        Edit
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!filteredProducts.length && !loading" class="empty-state">
              <v-icon size="36" color="grey">mdi-package-variant-closed</v-icon>
              <div class="empty-title">No products yet</div>
              <div class="empty-subtitle">Add your first item to start selling.</div>
              <v-btn small color="primary" class="mt-2" :to="{ name: 'business_seller.add_product' }">
                Add a Product
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SellerProducts',

  data() {
    return {
      products: [],
      search:   '',
      loading:  false,
      productsError: null,
      headers: [
        { text: 'Image',           value: 'get_image', sortable: false },
        { text: 'Product',         value: 'name' },
        { text: 'Category',        value: 'category' },
        { text: 'Price (XAF)',     value: 'price' },
        { text: 'Wholesale (XAF)', value: 'wholesale_price' },
        { text: 'Stock',           value: 'quantity' },
        { text: 'Sold',            value: 'no_bought' },
        { text: 'Date',            value: 'get_date_str' },
        { text: 'Actions',         value: 'actions', sortable: false },
      ],
    }
  },

  computed: {
    filteredProducts() {
      const q = (this.search || '').toLowerCase().trim()
      if (!q) return this.products
      return this.products.filter(p => {
        return (
          String(p.name || '').toLowerCase().includes(q) ||
          String(p.category || '').toLowerCase().includes(q) ||
          String(p.price || '').toLowerCase().includes(q)
        )
      })
    },
  },

  mounted() {
    document.title = 'Seller | My Products'
    this.loadProducts()
  },

  methods: {
    async loadProducts() {
      this.loading = true
      this.productsError = null
      try {
        const res    = await axios.get('/api/v1/market_place/seller/products/')
        if (!Array.isArray(res.data)) {
          this.products = []
          this.productsError = 'Unexpected response while loading products.'
          return
        }
        this.products = res.data
      } catch (err) {
        this.products = []
        this.productsError = err.response?.data?.error || 'Unable to load products.'
        const msg = err.response
          ? `${err.response.status} ${err.response.statusText}`
          : 'An error occurred. Please check your connection.'
        this.$store.commit('setSnackBarMessage', msg)
        this.$store.commit('setSnackBarColor',   'red darken-4')
        this.$store.commit('activateSnackBar',   true)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.seller-products {
  min-height: 100vh;
  background:
    radial-gradient(1200px 600px at 90% -10%, rgba(34,197,94,0.15), transparent),
    linear-gradient(135deg, #f7f8fb 0%, #eef2f7 100%);
}

.hero {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  padding: 28px 24px;
  background: linear-gradient(135deg, #0f172a 0%, #14532d 70%, #22c55e 140%);
  color: #fff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.25);
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(255,255,255,0.18);
  margin-bottom: 8px;
}

.hero-title {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 6px;
}

.hero-subtitle {
  margin: 0 0 16px;
  opacity: 0.9;
  max-width: 560px;
}

.hero-glow {
  position: absolute;
  right: -120px;
  top: -120px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(34,197,94,0.5), transparent 70%);
  filter: blur(4px);
}

.table-section {
  margin-top: 20px;
}

.table-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #eef1f6;
}

.table-wrapper {
  overflow-x: auto;
}

.seller-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.seller-table th,
.seller-table td {
  padding: 12px 14px;
  border-top: 1px solid #eef1f6;
  vertical-align: middle;
  text-align: left;
}

.seller-table thead th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  background: #fafbff;
}

.seller-table tbody tr:hover {
  background: #f9fafb;
}

.table-title {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 8px;
}

.title-text {
  font-weight: 700;
}

.subtitle-text {
  font-size: 12px;
  color: #6b7280;
}

.search-field {
  max-width: 260px;
}

.thumb {
  border-radius: 8px;
  border: 1px solid #eef1f6;
}

.table-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.empty-state {
  text-align: center;
  padding: 24px 12px;
  color: #6b7280;
}

.empty-title {
  margin-top: 8px;
  font-weight: 600;
  color: #111827;
}

.empty-subtitle {
  font-size: 12px;
  margin-top: 2px;
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 22px;
  }
}
</style>
